import React, { useEffect, useState } from "react";
import "./Selections.css";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../FreshersRegistration/constant";

const SHARE_TEXT = `👋 Hey! I’m going for the RCA Freshers’ Selection and I’d love for you to join too! 🤗

Let’s experience the fun, culture, and amazing bonding together in Rajasthan Culture Association at IIT Kharagpur. 🌸✨

It’s not just about selections, it’s about making memories and being part of a family! 👫🤝

Register here and let’s rock it together: 
https://www.rcaiitkgp.com/FreshersRegistration 🚀

No experience needed, just bring your energy and excitement! 💛
`;

function getTimeLeft(target) {
    const now = new Date();
    const end = new Date(target);
    const diff = end - now;
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, ended: true };
    return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
        ended: false,
    };
}

function Timer({ target, label, startTime = "17:30", endTime = "20:00" }) {
    const [time, setTime] = useState(() => getTimeLeft(target));
    const [isLive, setIsLive] = useState(false);
    const [ended, setEnded] = useState(false);
    const [showLiveMsg, setShowLiveMsg] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            const now = new Date();
            const eventDate = new Date(target);
            const eventDay = eventDate.toDateString();
            const start = new Date(`${eventDay} ${startTime}:00 GMT+0530`);
            const end = new Date(`${eventDay} ${endTime}:00 GMT+0530`);

            if (now >= start && now <= end) {
                setIsLive(true);
                setEnded(false);
            } else if (now > end) {
                setIsLive(false);
                setEnded(true);
            } else {
                setIsLive(false);
                setEnded(false);
            }
            setTime(getTimeLeft(target));
        }, 1000);
        return () => clearInterval(interval);
    }, [target, startTime, endTime]);

    return (
        <div
            className={`selection-timer${isLive ? " timer-live" : ""}${ended ? " timer-ended-bg" : ""}`}
            onMouseEnter={() => isLive && setShowLiveMsg(true)}
            onMouseLeave={() => setShowLiveMsg(false)}
        >
            <div className="timer-label">{label}</div>
            {ended ? (
                <span className="timer-ended">Ended</span>
            ) : isLive ? (
                <>
                    <span className="timer-live-msg">
                        <span className="pulse-dot"></span>
                        LIVE NOW!
                    </span>
                    {showLiveMsg && (
                        <div className="timer-live-tooltip">
                            Selection is <b>LIVE!</b> Head to the venue and give your best!
                        </div>
                    )}
                </>
            ) : time.ended ? (
                <span className="timer-ended">Started!</span>
            ) : (
                <span className="timer-values">
                    {time.days > 0 && <span>{time.days}d </span>}
                    <span>{String(time.hours).padStart(2, "0")}h </span>
                    <span>{String(time.minutes).padStart(2, "0")}m </span>
                    <span>{String(time.seconds).padStart(2, "0")}s</span>
                </span>
            )}
        </div>
    );
}

function ImageModal({ src, alt, open, onClose }) {
    if (!open) return null;
    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-image-container" onClick={e => e.stopPropagation()}>
                <img src={src} alt={alt} className="modal-image" />
                <button className="modal-close-btn" onClick={onClose}>&times;</button>
            </div>
        </div>
    );
}

function Selections() {
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState(false);
    const [settings, setSettings] = useState(null);
    const [settingsError, setSettingsError] = useState("");

    useEffect(() => {
        let cancelled = false;
        fetch(`${BASE_URL}/selection-settings`)
            .then((res) => res.json())
            .then((data) => {
                if (cancelled) return;
                if (data.status === "Success" && data.settings) {
                    setSettings(data.settings);
                    setSettingsError("");
                } else {
                    setSettingsError(data.message || "Could not load selection details");
                }
            })
            .catch(() => {
                if (!cancelled) setSettingsError("Could not load selection details");
            });
        return () => { cancelled = true; };
    }, []);

    const handleShare = async () => {
        if (navigator.share) {
            await navigator.share({
                title: "RCA Freshers' Selection – Join Me!",
                text: SHARE_TEXT
            });
        } else {
            await navigator.clipboard.writeText(SHARE_TEXT);
            alert("Share message copied! You can now paste it to your friends.");
        }
    };

    const venueText = settings?.venueDisplay
        || (settings ? `${settings.venue}, ${settings.startTime} onwards` : "Loading venue...");

    return (
        <div className="selection-main-card">
            <div className="selection-poster">
                <img
                    src="/freshers-selection.png"
                    alt="Freshers Selection Poster"
                    onClick={() => setModalOpen(true)}
                    style={{ cursor: "pointer" }}
                    title="Click to enlarge"
                />
            </div>
            <div className="selection-content">
                <h2 className="selection-welcome">Welcome, Freshers!</h2>
                <p className="selection-motivation">
                    Step into the vibrant world of <span className="rca-highlight">Rajasthan Culture Association</span>.<br />
                    <span className="rca-highlight">Join the team</span> and be a part of a legacy that celebrates tradition, unity, and creativity.<br />
                    <span className="rca-highlight">Unleash your potential</span>, make lifelong friends, and create memories that last forever.<br />
                    <span className="rca-highlight">Your journey begins here!</span>
                </p>
                <div className="selection-info-row">
                    <div className="selection-location">
                        <span className="location-icon" role="img" aria-label="location">📍</span>
                        <span className="location-text">{settingsError || venueText}</span>
                    </div>
                    <div className="selection-days-timers">
                        {settings?.days?.length > 0 ? (
                            settings.days.map((d, i) => (
                                <Timer
                                    key={`${d.label}-${i}`}
                                    target={d.datetime}
                                    label={d.label}
                                    startTime={settings.startTime}
                                    endTime={settings.endTime}
                                />
                            ))
                        ) : (
                            !settingsError && <span className="location-text">Loading timers...</span>
                        )}
                    </div>
                </div>
                <div className="selection-btn-row">
                    <button
                        className="selection-register-btn"
                        onClick={() => navigate("/FreshersRegistration")}
                    >
                        Register Now
                    </button>
                    <button
                        className="selection-share-btn"
                        onClick={handleShare}
                    >
                        Invite a Friend
                    </button>
                </div>
            </div>
            <ImageModal
                src="/freshers-selection.png"
                alt="Freshers Selection Poster"
                open={modalOpen}
                onClose={() => setModalOpen(false)}
            />
        </div>
    );
}

export default Selections;
